/**
 * Le seuil d'apparition du squelette d'une page.
 *
 * Une réponse qui revient en quelques dizaines de millisecondes ne doit rien faire clignoter : tant
 * que le chargement n'a pas duré `SKELETON_APPEAR_DELAY_MS`, la zone de contenu reste vide, sur le
 * fond de l'écran. Passé le seuil, le squelette paraît et reste jusqu'au contenu. C'est une durée
 * de PERCEPTION, pas de mouvement : « Réduire les animations » ne la met pas à zéro.
 *
 * Un réducteur pur, pour que la règle se teste sans horloge ni rendu (`skeletonGate.test.ts`) ;
 * `hooks/useSkeletonGate` n'y ajoute que le minuteur.
 */

/** Sous ce délai, une page qui charge ne montre rien de plus que son fond. */
export const SKELETON_APPEAR_DELAY_MS = 150

/**
 * `idle` : rien ne charge · `waiting` : ça charge, le seuil court encore — la page reste vide ·
 * `shown` : le seuil est passé — la page montre son squelette.
 */
export type SkeletonGate = "idle" | "waiting" | "shown"

/** `loading` : le chargement commence · `elapsed` : le seuil est passé · `loaded` : il est fini. */
export type SkeletonGateEvent = "loading" | "elapsed" | "loaded"

export function nextSkeletonGate(gate: SkeletonGate, event: SkeletonGateEvent): SkeletonGate {
    switch (event) {
        // Un second « ça charge » ne relance pas le seuil : un squelette déjà là y reste.
        case "loading":
            return gate === "idle" ? "waiting" : gate
        // Un minuteur en retard, arrivé après la fin du chargement, ne rallume rien.
        case "elapsed":
            return gate === "waiting" ? "shown" : gate
        case "loaded":
            return "idle"
    }
}

/** L'état de départ : une page montée en plein chargement attend son seuil comme les autres. */
export function initialSkeletonGate(loading: boolean): SkeletonGate {
    return loading ? "waiting" : "idle"
}
